import React, { useMemo } from "react";
import { motion } from "framer-motion";

const seed = (s) => {
    let n = s;
    return () => {
        n = (n * 9301 + 49297) % 233280;
        return n / 233280;
    };
};

const Aurora = ({ opacity }) => (
    <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ opacity }}
    >
        <motion.div
            className="absolute -top-1/3 -left-1/4 w-[600px] h-[600px]"
            style={{
                background: "radial-gradient(closest-side, rgba(110,60,255,0.45), transparent 70%)",
                filter: "blur(80px)",
            }}
            animate={{ x: [0, 100, -40, 0], y: [0, 80, -60, 0] }}
            transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
            className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px]"
            style={{
                background: "radial-gradient(closest-side, rgba(0,229,255,0.32), transparent 70%)",
                filter: "blur(80px)",
            }}
            animate={{ x: [0, -120, 40, 0], y: [0, -60, 80, 0] }}
            transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        />
    </div>
);

const Dots = ({ opacity }) => {
    const dots = useMemo(() => {
        const r = seed(11);
        return Array.from({ length: 70 }, (_, i) => ({
            id: i,
            x: r() * 100,
            y: r() * 100,
            delay: r() * 6,
            duration: 4 + r() * 4,
        }));
    }, []);
    return (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" style={{ opacity }}>
            {dots.map((d) => (
                <motion.span
                    key={d.id}
                    className="absolute h-1 w-1 rounded-full bg-cyan/50"
                    style={{ left: `${d.x}%`, top: `${d.y}%` }}
                    animate={{ opacity: [0.1, 0.7, 0.1], scale: [0.8, 1.4, 0.8] }}
                    transition={{ duration: d.duration, repeat: Infinity, ease: "easeInOut", delay: d.delay }}
                />
            ))}
            <motion.div
                className="absolute inset-0"
                style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(110,60,255,0.18), transparent 60%)" }}
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />
        </div>
    );
};

const Grid = ({ opacity }) => (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" style={{ opacity }}>
        <motion.div
            className="absolute inset-0"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)",
                backgroundSize: "72px 72px",
                maskImage: "radial-gradient(ellipse 65% 65% at 50% 50%, #000 30%, transparent 80%)",
                WebkitMaskImage: "radial-gradient(ellipse 65% 65% at 50% 50%, #000 30%, transparent 80%)",
            }}
            animate={{ backgroundPositionY: ["0px", "72px"] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
            className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-cyan/40 to-transparent"
            animate={{ left: ["0%", "100%"] }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
    </div>
);

const Streams = ({ opacity }) => {
    const lines = useMemo(() => {
        const r = seed(7);
        return Array.from({ length: 6 }, (_, i) => ({
            id: i,
            top: 8 + r() * 84,
            duration: 14 + r() * 14,
            delay: r() * 8,
            direction: i % 2 === 0 ? 1 : -1,
            width: 30 + r() * 30,
            color: i % 2 === 0 ? "rgba(0,229,255,0.45)" : "rgba(110,60,255,0.45)",
        }));
    }, []);
    return (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" style={{ opacity }}>
            {lines.map((l) => (
                <motion.div
                    key={l.id}
                    className="absolute h-px"
                    style={{
                        top: `${l.top}%`,
                        width: `${l.width}%`,
                        background: `linear-gradient(${l.direction > 0 ? "90deg" : "270deg"}, transparent, ${l.color}, transparent)`,
                    }}
                    initial={{ x: l.direction > 0 ? "-30vw" : "100vw" }}
                    animate={{ x: l.direction > 0 ? "120vw" : "-130vw" }}
                    transition={{ duration: l.duration, repeat: Infinity, ease: "linear", delay: l.delay }}
                />
            ))}
            <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_50%,#000,transparent)]">
                <div className="absolute inset-0 grid-lines-bg" style={{ opacity: 0.25 }} />
            </div>
        </div>
    );
};

const Orbs = ({ opacity }) => (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" style={{ opacity }}>
        {[
            { x: "10%", y: "20%", size: 280, color: "rgba(110,60,255,0.30)", d: 18 },
            { x: "85%", y: "60%", size: 320, color: "rgba(0,229,255,0.22)", d: 22 },
            { x: "55%", y: "12%", size: 220, color: "rgba(110,60,255,0.20)", d: 26 },
        ].map((o, i) => (
            <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                    left: o.x,
                    top: o.y,
                    width: o.size,
                    height: o.size,
                    background: `radial-gradient(closest-side, ${o.color}, transparent 70%)`,
                    filter: "blur(60px)",
                }}
                animate={{ x: [0, 60, -40, 0], y: [0, -40, 60, 0], scale: [1, 1.1, 0.95, 1] }}
                transition={{ duration: o.d, repeat: Infinity, ease: "easeInOut", delay: i * 2 }}
            />
        ))}
    </div>
);

const VARIANTS = { aurora: Aurora, dots: Dots, grid: Grid, streams: Streams, orbs: Orbs };

export const AmbientBackground = ({ variant = "aurora", opacity = 1 }) => {
    const Cmp = VARIANTS[variant] || Aurora;
    return <Cmp opacity={opacity} />;
};

export default AmbientBackground;
